// Seitennavigation — 240px auf dem Desktop, Schublade auf dem Handy.
//
// Aufbau (Paket 2 des Redesigns):
//   1. Hauptziele   — Projektion von `primaryNav()` (lib/navigation.ts).
//   2. Arbeitsbereich — ⭐ Angeheftet, Aufgabenlisten, Projekte (Space →
//      Ordner → Projekt). Dieser Baum-Teil ist UNVERÄNDERT: er hat zwei
//      Fehlerbehebungs-Deploys (#106/#107, Drag&Drop Aufgabe→Liste) hinter
//      sich und wird nicht umgeschrieben.
//   3. Fussbereich  — Projektion von `footerNav(isAdmin)`.
//
// Vorher trug diese Datei 12 handgepflegte Top-Level-Ziele ("Projekte" sogar
// doppelt) und MobileNav.tsx eine zweite, abweichende Liste. Beide Blöcke
// kommen jetzt aus lib/navigation.ts; eine zweite Definitionsstelle gibt es
// nicht mehr.
//
// NICHT mehr hier: Suche-Auslöser, NotificationBell und der Nutzer-Block
// (Name/E-Mail/Abmelden) — die sitzen in der App-Kopfzeile (AppHeader.tsx).
// Auch die beiden mobil frei schwebenden Knöpfe sind weg; mobil öffnet die
// Kopfzeile die Schublade über `sidebarOpenSig`.

import { useState, useEffect } from "preact/hooks";
import { Link, useLocation } from "wouter-preact";
import {
  ChevronDown,
  ChevronRight,
  Folder,
  FolderPlus,
  ListTodo,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeftOpen,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-preact";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import type { ProjectListItem, Space, TaskList } from "@juuplan/shared";
import { useMe } from "../lib/auth";
import { useProjects, refreshProjects } from "../store/projects";
import { useSpaces, createSpace } from "../store/spaces";
import { useFavorites, toggleFavorite } from "../store/favorites";
import { useUserPrefs } from "../store/userPrefs";
import { isProjectVisible } from "../lib/departmentFilter";
import { confirmDialog } from "../lib/confirm";
import { PROJECT_STATUS_VAR, COLOR_PRESETS } from "../lib/format";
import { NewProjectModal } from "./NewProjectModal";
import { NewPersonalTaskModal } from "./NewPersonalTaskModal";
import {
  saveSidebarCollapsed,
  useSidebarCollapsed,
} from "../lib/sidebarState";
import {
  useProjectSections,
  toggleProjectSection,
} from "../lib/projectSections";
import { useFolders, addFolder } from "../store/folders";
import { useLists, createList, updateList, deleteList } from "../store/personalTasks";
import { LIST_DROP_ATTR, listDropActiveSig, listDropHoverSig } from "../store/listDrop";
import { moveProject } from "../lib/api";
import { toastError } from "../lib/toast";
import { signal } from "@preact/signals";
import { refreshInboxCount } from "../lib/inboxBadge";
import { footerNav, primaryNav } from "../lib/navigation";
// Geteilter Schublade-Zustand: die App-Kopfzeile (AppHeader.tsx) öffnet die
// Sidebar auf dem Handy, deshalb liegt der Zustand nicht mehr lokal hier.
import { sidebarOpenSig } from "../lib/pageHeader";
import { SidebarNavList } from "./sidebar/SidebarNav";

import { Button } from "./ui/Button";

// Note: inbox count is fetched inside the Sidebar component via a useEffect
// that is gated on auth state. The event listener for juuplan:inbox-refresh
// is also registered there so it only runs when a user is authenticated.

// ---- Space-group collapse state (persisted per space id) -------------------

const SPACE_GROUP_PREFIX = "juuplan:space-group:";

function readSpaceCollapsed(id: number): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(SPACE_GROUP_PREFIX + id) === "1";
}

// One signal holding a map of spaceId -> collapsed.
// Lazily populated as spaces are rendered.
const spaceCollapsedSig = signal<Record<number, boolean>>({});

function useSpaceCollapsed(id: number): boolean {
  // Pure read — no signal write during render.
  // Falls back to localStorage (or false) without mutating the signal.
  return spaceCollapsedSig.value[id] ?? readSpaceCollapsed(id);
}

function toggleSpaceCollapsed(id: number): void {
  const cur = spaceCollapsedSig.value[id] ?? readSpaceCollapsed(id);
  const next = !cur;
  spaceCollapsedSig.value = { ...spaceCollapsedSig.value, [id]: next };
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(SPACE_GROUP_PREFIX + id, next ? "1" : "0");
  }
}

// ---- Folder-group collapse state (persisted per folder id) -----------------

const FOLDER_GROUP_PREFIX = "juuplan:folder-group:";

function readFolderCollapsed(id: number): boolean {
  if (typeof localStorage === "undefined") return false;
  // Folders default to expanded (not collapsed), so absence == expanded.
  return localStorage.getItem(FOLDER_GROUP_PREFIX + id) === "1";
}

// One signal holding a map of folderId -> collapsed.
const folderCollapsedSig = signal<Record<number, boolean>>({});

function useFolderCollapsed(id: number): boolean {
  // Pure read — no signal write during render.
  // Falls back to localStorage (or false) without mutating the signal.
  return folderCollapsedSig.value[id] ?? readFolderCollapsed(id);
}

function toggleFolderCollapsed(id: number): void {
  const cur = folderCollapsedSig.value[id] ?? readFolderCollapsed(id);
  const next = !cur;
  folderCollapsedSig.value = { ...folderCollapsedSig.value, [id]: next };
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(FOLDER_GROUP_PREFIX + id, next ? "1" : "0");
  }
}

// ---- Shared tree-row visuals (Projekte + Listen use the exact same classes
// so the two trees read as one system) ------------------------------------

function treeRowClass(indentClass: string, active: boolean): string {
  return `group inline-flex items-center gap-2 rounded ${indentClass} pr-2.5 py-1 text-sm w-full min-h-[28px] transition-colors ${
    active
      ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)] font-medium"
      : "text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)]"
  }`;
}

function TreeRowContent(props: {
  leading?: preact.JSX.Element;
  colorDot?: string;
  label: string;
  /** Optional muted second line, e.g. the space a shared list belongs to. */
  subLabel?: string;
}): preact.JSX.Element {
  return (
    <>
      {props.leading}
      {props.colorDot && (
        <span
          class="inline-block size-2 rounded-full shrink-0"
          style={{ background: props.colorDot }}
          aria-hidden="true"
        />
      )}
      {props.subLabel ? (
        <span class="flex flex-col min-w-0 flex-1 leading-tight">
          <span class="truncate" title={props.label}>{props.label}</span>
          <span class="truncate text-label text-[color:var(--color-on-surface-variant)]/70">
            {props.subLabel}
          </span>
        </span>
      ) : (
        <span class="truncate flex-1" title={props.label}>{props.label}</span>
      )}
    </>
  );
}

// ---- FolderGroup component (module-scope to preserve identity across renders) ---

interface FolderGroupProps {
  folder: import("@juuplan/shared").ApiFolder;
  spaceProjects: import("@juuplan/shared").ProjectListItem[];
  activeProjectId: number | null | undefined;
  renderProjectLink: (
    p: import("@juuplan/shared").ProjectListItem,
    opts?: { favorite?: boolean; indent?: boolean; deepIndent?: boolean; draggableIndex?: number }
  ) => preact.JSX.Element;
  close: () => void;
}

function FolderGroup({ folder, spaceProjects, renderProjectLink }: FolderGroupProps): preact.JSX.Element {
  const folderProjects = spaceProjects.filter((p) => p.folder_id === folder.id);
  const isFolderCollapsed = useFolderCollapsed(folder.id);
  const droppableId = `folder-${folder.id}`;
  return (
    <li>
      <button
        type="button"
        onClick={() => toggleFolderCollapsed(folder.id)}
        class="w-full flex items-center gap-1.5 pl-7 pr-2.5 py-1 text-xs text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors min-h-[28px]"
        aria-expanded={!isFolderCollapsed}
        aria-label={`Ordner ${folder.name}`}
      >
        {isFolderCollapsed ? (
          <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
        ) : (
          <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
        )}
        <Folder size={14} aria-hidden="true" class="shrink-0" />
        <span class="truncate flex-1 text-left" title={folder.name}>{folder.name}</span>
        {folderProjects.length > 0 && (
          <span class="ml-auto tabular-nums text-label text-[color:var(--color-on-surface-variant)]/70">
            {folderProjects.length}
          </span>
        )}
      </button>
      {/* Droppable always rendered (even when collapsed) so the library can
          compute drop targets; visually hidden via isFolderCollapsed. */}
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <ul
            ref={provided.innerRef}
            {...provided.droppableProps}
            class={`flex flex-col gap-0.5 ${
              isFolderCollapsed && !snapshot.isDraggingOver ? "hidden" : ""
            } ${snapshot.isDraggingOver ? "bg-[color:var(--color-primary)]/5 rounded" : ""}`}
          >
            {folderProjects.map((p, idx) =>
              renderProjectLink(p, { indent: false, deepIndent: true, draggableIndex: idx })
            )}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </li>
  );
}

// ---- SpaceGroup component (module-scope to preserve identity across renders) ---

interface SpaceGroupProps {
  space: Space;
  visibleProjects: import("@juuplan/shared").ProjectListItem[];
  activeProjectId: number | null | undefined;
  renderProjectLink: (
    p: import("@juuplan/shared").ProjectListItem,
    opts?: { favorite?: boolean; indent?: boolean; deepIndent?: boolean; draggableIndex?: number }
  ) => preact.JSX.Element;
  close: () => void;
  /** When set, user is not external — show space-level creation affordances. */
  isInternal?: boolean;
  /** Callback to open NewProjectModal pre-scoped to this space. */
  onNewProject?: (spaceId: number) => void;
}

function SpaceGroup({
  space,
  visibleProjects,
  activeProjectId,
  renderProjectLink,
  close,
  isInternal,
  onNewProject,
}: SpaceGroupProps): preact.JSX.Element | null {
  const spaceProjects = visibleProjects.filter((p) => p.space_id === space.id);
  // All hooks must be called unconditionally before any early return.
  const folders = useFolders(space.id);
  const isCollapsed = useSpaceCollapsed(space.id);

  // Inline folder creation state local to this space group.
  const [showAddFolder, setShowAddFolder] = useState(false);
  const [addFolderName, setAddFolderName] = useState("");
  const [addFolderSaving, setAddFolderSaving] = useState(false);
  // Show space only if it has projects or folders (or folders are still loading).
  if (spaceProjects.length === 0 && !folders.loading && folders.items.length === 0) return null;

  const handleAddFolder = async (): Promise<void> => {
    const name = addFolderName.trim();
    if (!name) return;
    setAddFolderSaving(true);
    try {
      await addFolder(space.id, { name });
      setAddFolderName("");
      setShowAddFolder(false);
    } catch {
      // api wrapper already toasted
    } finally {
      setAddFolderSaving(false);
    }
  };

  const spaceColor = space.color ?? undefined;
  const iconStyle = spaceColor ? { color: spaceColor } : undefined;

  // Partition: unfoldered projects (folder_id === null) + per-folder projects.
  const unfolderedProjects = spaceProjects.filter((p) => p.folder_id === null);
  // folders already sorted by sort_order asc then id asc in the store.
  const sortedFolders = folders.items;

  /**
   * Droppable id scheme (within this space's DragDropContext):
   *   - folder-{folderId}      → project list inside a specific folder
   *   - unfoldered-{spaceId}  → the top-level unfoldered project area
   * Draggable id scheme: project-{projectId}
   */
  function handleDragEnd(result: DropResult): void {
    const { draggableId, destination, source } = result;
    if (!destination) return; // dropped outside
    if (destination.droppableId === source.droppableId) return; // same bucket, no reparent needed

    const projectId = parseInt(draggableId.replace("project-", ""), 10);
    if (isNaN(projectId)) return;

    const destId = destination.droppableId;
    const targetFolderId: number | null = destId.startsWith("folder-")
      ? parseInt(destId.replace("folder-", ""), 10)
      : null;

    void (async () => {
      try {
        await moveProject(projectId, { space_id: space.id, folder_id: targetFolderId });
        await refreshProjects();
      } catch (_err) {
        toastError("Projekt konnte nicht verschoben werden.");
        await refreshProjects();
      }
    })();
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div>
        {/* Space header row — collapse toggle + hover affordances */}
        <div
          class="flex items-center"
        >
          <button
            type="button"
            onClick={() => toggleSpaceCollapsed(space.id)}
            class="flex-1 flex items-center gap-1.5 pl-3 pr-1 pt-2 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors min-w-0"
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
            ) : (
              <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
            )}
            {space.icon ? (
              <span class="text-micro leading-none shrink-0" aria-hidden="true">
                {space.icon}
              </span>
            ) : (
              <Folder size={14} aria-hidden="true" class="shrink-0" style={iconStyle} />
            )}
            <span class="truncate flex-1 text-left" style={iconStyle}>
              {space.name}
            </span>
            {(
              <span class="ml-1 tabular-nums text-[color:var(--color-on-surface-variant)]/70">
                {spaceProjects.length}
              </span>
            )}
          </button>
          {/* Per-space affordance buttons — always visible, internal users only */}
          {isInternal && (
            <div class="flex items-center gap-0.5 pr-1 shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAddFolder((v) => !v);
                  setAddFolderName("");
                }}
                class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                aria-label={`Neuer Ordner in ${space.name}`}
                title="Neuer Ordner"
              >
                <FolderPlus size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onNewProject?.(space.id);
                  close();
                }}
                class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                aria-label={`Neues Projekt in ${space.name}`}
                title="Neues Projekt"
              >
                <Plus size={16} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        {/* Inline "Neuer Ordner" form — shown below space header */}
        {showAddFolder && isInternal && (
          <div class="flex items-center gap-1 px-3 pb-1">
            <input
              type="text"
              value={addFolderName}
              maxLength={100}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onInput={(e) => setAddFolderName((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); void handleAddFolder(); }
                if (e.key === "Escape") { setShowAddFolder(false); setAddFolderName(""); }
              }}
              placeholder="Ordnername…"
              class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-0.5 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
              aria-label="Name des neuen Ordners"
            />
            <button
              type="button"
              onClick={() => void handleAddFolder()}
              disabled={!addFolderName.trim() || addFolderSaving}
              class="rounded bg-[color:var(--color-primary)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-50 transition-colors"
              aria-label="Ordner erstellen"
            >
              {addFolderSaving ? "…" : "OK"}
            </button>
            <button
              type="button"
              onClick={() => { setShowAddFolder(false); setAddFolderName(""); }}
              class="rounded border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-label text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
              aria-label="Abbrechen"
            >
              ✕
            </button>
          </div>
        )}
        {!isCollapsed && (
          <div class="flex flex-col gap-0.5 pb-1">
            {/* Unfoldered projects — top-level droppable for this space */}
            <Droppable droppableId={`unfoldered-${space.id}`}>
              {(provided, snapshot) => (
                <ul
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  class={`flex flex-col gap-0.5 ${
                    snapshot.isDraggingOver ? "bg-[color:var(--color-primary)]/5 rounded" : ""
                  }`}
                >
                  {unfolderedProjects.map((p, idx) =>
                    renderProjectLink(p, { indent: true, draggableIndex: idx })
                  )}
                  {provided.placeholder}
                </ul>
              )}
            </Droppable>
            {/* Folder groups — each has its own Droppable internally */}
            {sortedFolders.map((folder) => (
              <FolderGroup
                key={folder.id}
                folder={folder}
                spaceProjects={spaceProjects}
                activeProjectId={activeProjectId}
                renderProjectLink={renderProjectLink}
                close={close}
              />
            ))}
          </div>
        )}
      </div>
    </DragDropContext>
  );
}

// ---- List selection (derived from the URL, mirrors activeProjectId) --------

/** Eine konkrete Liste oder „Alle Aufgaben". Die frühere Pseudo-Liste
 *  „Ohne Liste" (`null`, /lists/none) ist weg — jede Aufgabe hat eine Liste. */
type ListSelection = number | "all";

/** Parses the current location into the Lists route's selection, or
 * `undefined` when the user isn't on /lists at all. */
function matchListSelection(location: string): ListSelection | undefined {
  if (location === "/lists") return "all";
  const m = /^\/lists\/(\d+)$/.exec(location);
  if (m) return Number(m[1]);
  return undefined;
}

// ---- ListRow (module-scope to preserve identity across renders) ------------
// Single-depth leaf row for a personal/shared list — same row visuals as a
// project, plus a hover-revealed "…" menu (Umbenennen/Löschen) since lists
// have no separate settings page the way projects do.

function ListRow({
  list,
  active,
  close,
}: {
  list: TaskList;
  active: boolean;
  close: () => void;
}): preact.JSX.Element {
  const [, setLocation] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(list.name);
  const favorites = useFavorites();
  const isFav = favorites.list.has(list.id);

  // Drop target for a task dragged out of another list (see store/listDrop).
  // Viewer-role shared lists are not offered: the server would 403 the move, so
  // the row must not promise it. The rect the pointer is hit-tested against is
  // the <li>; the highlight sits on the row itself.
  const droppable =
    list.scope !== "space" || list.role === "owner" || list.role === "editor";
  const dropActive = droppable && listDropActiveSig.value;
  const dropHovered = dropActive && listDropHoverSig.value === list.id;

  async function commitRename(): Promise<void> {
    setRenaming(false);
    const v = renameValue.trim();
    if (v && v !== list.name) await updateList(list.id, { name: v });
  }

  async function handleDelete(): Promise<void> {
    setMenuOpen(false);
    const ok = await confirmDialog({
      title: "Liste löschen",
      message: `Liste „${list.name}" in den Papierkorb verschieben?`,
      confirmLabel: "In den Papierkorb",
      danger: true,
    });
    if (!ok) return;
    await deleteList(list.id);
    if (active) setLocation("/lists");
  }

  if (renaming) {
    return (
      <li>
        <input
          type="text"
          value={renameValue}
          aria-label="Liste umbenennen"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          onInput={(e) => setRenameValue((e.target as HTMLInputElement).value)}
          onBlur={() => void commitRename()}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setRenaming(false);
          }}
          class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-1 text-base sm:text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
        />
      </li>
    );
  }

  return (
    <li
      class={`group relative ${menuOpen ? "z-10" : ""}`}
      {...(droppable ? { [LIST_DROP_ATTR]: String(list.id) } : {})}
    >
      <Link
        href={`/lists/${list.id}`}
        onClick={close}
        class={`${treeRowClass("pl-7", active)} ${
          dropHovered
            ? "ring-2 ring-[color:var(--color-primary)] bg-[color:var(--color-primary)]/15"
            : dropActive
              ? "ring-1 ring-[color:var(--color-outline-variant)]"
              : ""
        }`}
      >
        <TreeRowContent
          colorDot={list.color ?? "var(--color-primary)"}
          label={list.name}
          subLabel={
            list.scope === "space"
              ? (list.space_name ?? "Geteilter Space")
              : undefined
          }
        />
      </Link>
      <div
        class="absolute right-1 top-1/2 -translate-y-1/2"
        onKeyDown={(e) => {
          if (e.key === "Escape" && menuOpen) {
            e.stopPropagation();
            setMenuOpen(false);
          }
        }}
      >
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          class={`rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] ${
            menuOpen ? "flex" : "hidden max-md:flex group-hover:flex group-focus-within:flex"
          }`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Optionen für ${list.name}`}
        >
          <MoreHorizontal size={14} aria-hidden="true" />
        </button>
        {menuOpen && (
          <>
            <div class="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} aria-hidden="true" />
            <div
              role="menu"
              class="absolute right-0 mt-1 z-40 w-36 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 py-1 text-xs"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  void toggleFavorite("list", list.id).catch(() => {
                    /* toggleFavorite() hat schon gemeldet */
                  });
                }}
                class="w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)] text-[color:var(--color-on-surface)] flex items-center gap-1.5"
              >
                <Star size={14} aria-hidden="true" fill={isFav ? "currentColor" : "none"} />
                {isFav ? "Favorit entfernen" : "Zu Favoriten"}
              </button>
              <div class="my-1 border-t border-[color:var(--color-outline-variant)]" />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  setRenameValue(list.name);
                  setRenaming(true);
                }}
                class="w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-surface-container-low)] text-[color:var(--color-on-surface)] flex items-center gap-1.5"
              >
                <Pencil size={14} aria-hidden="true" />
                Umbenennen
              </button>
              <div class="my-1 border-t border-[color:var(--color-outline-variant)]" />
              <div class="px-3 py-1.5">
                <div class="mb-1.5 text-[color:var(--color-on-surface-variant)]">Farbe</div>
                <div class="flex flex-wrap gap-1" role="group" aria-label="Farbe wählen">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => void updateList(list.id, { color: c })}
                      class={`size-5 max-md:size-7 juuplan-no-min-height rounded-full border-2 transition-transform ${
                        (list.color ?? "") === c
                          ? "border-[color:var(--color-on-surface)] scale-110"
                          : "border-transparent hover:scale-110"
                      }`}
                      style={{ background: c }}
                      aria-label={`Farbe ${c}`}
                      aria-pressed={(list.color ?? "") === c}
                    />
                  ))}
                </div>
              </div>
              <div class="my-1 border-t border-[color:var(--color-outline-variant)]" />
              <button
                type="button"
                role="menuitem"
                onClick={() => void handleDelete()}
                class="w-full text-left px-3 py-1.5 hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] text-[color:var(--color-error)] flex items-center gap-1.5"
              >
                <Trash2 size={14} aria-hidden="true" />
                In den Papierkorb verschieben
              </button>
            </div>
          </>
        )}
      </div>
    </li>
  );
}

// ---- Component --------------------------------------------------------------

interface SidebarProps {
  /** Highlight a project as active (driven by route params). */
  activeProjectId?: number | null;
}

export function Sidebar(props: SidebarProps): preact.JSX.Element {
  const { activeProjectId } = props;
  const me = useMe();
  const projects = useProjects();
  const spaces = useSpaces();
  const favorites = useFavorites();
  const prefs = useUserPrefs();
  const sections = useProjectSections();
  const [location, setLocation] = useLocation();
  const { lists, loading: listsLoading } = useLists();

  const visibleProjects = projects.items.filter((p) =>
    isProjectVisible(p, prefs.visible_departments)
  );
  const favoriteProjects = visibleProjects.filter((p) =>
    favorites.project.has(p.id)
  );

  const [showCreate, setShowCreate] = useState(false);
  const [newProjectSpaceId, setNewProjectSpaceId] = useState<number | undefined>(undefined);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showCreatePersonalTask, setShowCreatePersonalTask] = useState(false);
  // Kein lokaler Offen-Zustand mehr: die App-Kopfzeile toggelt dasselbe
  // Signal, sonst hätte die Schublade zwei Wahrheiten.
  const drawerOpen = sidebarOpenSig.value;
  const collapsed = useSidebarCollapsed();

  // Escape closes the mobile drawer / create menu (backdrop click alone
  // leaves keyboard users without an exit).
  useEffect(() => {
    if (!drawerOpen && !showCreateMenu) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (showCreateMenu) setShowCreateMenu(false);
      else sidebarOpenSig.value = false;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen, showCreateMenu]);

  // "Neuer Space" inline form state (sidebar header affordance).
  const [showNewSpace, setShowNewSpace] = useState(false);
  const [newSpaceName, setNewSpaceName] = useState("");
  const [creatingSpace, setCreatingSpace] = useState(false);

  // "Neue Liste" inline form state (Listen-Sektion header affordance).
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListSpaceId, setNewListSpaceId] = useState<number | undefined>(undefined);
  const [creatingList, setCreatingList] = useState(false);

  const isInternal = me.user ? !me.user.is_external : false;

  const handleCreateSpace = async (): Promise<void> => {
    const name = newSpaceName.trim();
    if (!name) return;
    setCreatingSpace(true);
    try {
      await createSpace({ name, icon: null, color: COLOR_PRESETS[0] });
      setNewSpaceName("");
      setShowNewSpace(false);
    } catch {
      // api wrapper already toasted
    } finally {
      setCreatingSpace(false);
    }
  };

  const handleCreateList = async (): Promise<void> => {
    const name = newListName.trim();
    if (!name) return;
    setCreatingList(true);
    try {
      await createList(name, undefined, newListSpaceId);
      setNewListName("");
      setNewListSpaceId(undefined);
      setShowNewList(false);
    } catch {
      // api wrapper already toasted
    } finally {
      setCreatingList(false);
    }
  };

  // Inbox unread badge — only fetched when authenticated to prevent a
  // boot-time 401 on /login triggering a hard redirect and an infinite loop.
  useEffect(() => {
    if (!me.user) return;
    // Initial fetch right after mount (replaces the removed module-load setTimeout).
    refreshInboxCount();
    // Re-fetch whenever a notification event fires (dispatched by App.tsx SSE handler).
    window.addEventListener("juuplan:inbox-refresh", refreshInboxCount);
    return () => {
      window.removeEventListener("juuplan:inbox-refresh", refreshInboxCount);
    };
  }, [me.user]);

  const close = (): void => { sidebarOpenSig.value = false; };
  const toggleCollapse = (): void => saveSidebarCollapsed(!collapsed);
  const navWidthClass = collapsed ? "md:w-[64px]" : "md:w-[240px]";

  // Einzelne Projekt-Zeile.
  // When opts.draggableIndex is provided the row is wrapped in a Draggable
  // (used inside SpaceGroup's DragDropContext). Without it, renders a plain li
  // (e.g. in the favorites section, which is cross-space and not draggable).
  const renderProjectLink = (
    p: ProjectListItem,
    opts?: { favorite?: boolean; indent?: boolean; deepIndent?: boolean; draggableIndex?: number }
  ): preact.JSX.Element => {
    const active = p.id === activeProjectId;
    const indentClass = opts?.deepIndent ? "pl-11" : opts?.indent ? "pl-7" : "pl-5";

    const linkContent = (
      <Link href={`/p/${p.id}`} onClick={close} class={treeRowClass(indentClass, active)}>
        <TreeRowContent
          leading={
            opts?.favorite ? (
              <Star
                size={14}
                aria-hidden="true"
                fill="currentColor"
                class="text-[color:var(--color-warning)] shrink-0"
              />
            ) : undefined
          }
          colorDot={p.color ?? PROJECT_STATUS_VAR[p.status]}
          label={p.name}
        />
      </Link>
    );

    if (opts?.draggableIndex !== undefined) {
      return (
        <Draggable key={p.id} draggableId={`project-${p.id}`} index={opts.draggableIndex}>
          {(dragProvided, dragSnapshot) => (
            <li
              ref={dragProvided.innerRef as (el: HTMLElement | null) => void}
              {...(dragProvided.draggableProps as unknown as Record<string, unknown>)}
              {...((dragProvided.dragHandleProps ?? undefined) as Record<string, unknown> | undefined)}
              style={dragProvided.draggableProps.style as preact.JSX.CSSProperties | undefined}
              class={dragSnapshot.isDragging ? "opacity-70" : ""}
            >
              {linkContent}
            </li>
          )}
        </Draggable>
      );
    }

    return <li key={p.id}>{linkContent}</li>;
  };

  // „Angeheftet" — Sektion des Arbeitsbereichs (space-übergreifend, nur bei
  // Inhalt sichtbar). War bis Paket 2 das Top-Level-Ziel „Favoriten"
  // (/favorites). Der Pfad bleibt erreichbar: „Alle anzeigen" führt dorthin.
  const renderFavoritesSection = (): preact.JSX.Element | null => {
    if (favoriteProjects.length === 0) return null;
    const sectionCollapsed = sections.favorites;
    return (
      <div>
        <div class="flex items-center">
        <button
          type="button"
          onClick={() => toggleProjectSection("favorites")}
          class="flex-1 flex items-center gap-1.5 pl-2 pr-1 pt-1 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors min-w-0"
          aria-expanded={!sectionCollapsed}
        >
          {sectionCollapsed ? (
            <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
          ) : (
            <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
          )}
          <Star size={14} aria-hidden="true" class="shrink-0" />
          <span class="truncate">Angeheftet</span>
          <span class="ml-auto tabular-nums text-[color:var(--color-on-surface-variant)]/70">
            {favoriteProjects.length}
          </span>
        </button>
        <Link
          href="/favorites"
          onClick={close}
          class="mr-1 shrink-0 rounded px-1 py-0.5 text-label text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
          title="Alle angehefteten Elemente"
        >
          Alle anzeigen
        </Link>
        </div>
        {!sectionCollapsed && (
          <ul class="flex flex-col gap-0.5 pb-1">
            {favoriteProjects.map((p) => renderProjectLink(p, { favorite: true }))}
          </ul>
        )}
      </div>
    );
  };

  const rootCollapsed = sections.root;

  const listsRootCollapsed = sections.listsRoot;
  const listsSharedCollapsed = sections.listsShared;
  const activeListSelection = matchListSelection(location);
  const personalLists = lists.filter((l) => l.scope !== "space");
  const sharedLists = lists.filter((l) => l.scope === "space");

  return (
    <>
      {/* Backdrop (mobile only when drawer open) */}
      {drawerOpen && (
        <div
          class="md:hidden fixed inset-0 z-40 bg-[color:var(--color-on-surface)]/40 backdrop-blur-sm"
          onClick={close}
          aria-hidden="true"
        />
      )}

      <aside
        class={`jp-rail fixed md:sticky top-0 left-0 z-40 h-dvh w-[240px] ${navWidthClass} shrink-0 bg-[color:var(--color-sidebar)] flex flex-col transition-[transform,width] duration-300 ease-out overflow-hidden ${
          drawerOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
        aria-label="Hauptnavigation"
        data-collapsed={collapsed ? "1" : "0"}
      >
        {/* Marke + Einklapp-Schalter (Glocke/Suche/Nutzer: Kopfzeile) */}
        <div class={`pt-5 pb-3 flex items-start gap-2 ${collapsed ? "px-3 justify-center" : "px-5 justify-between"}`}>
          <Link href="/dashboard" class="block min-w-0" onClick={close}>
            {collapsed ? (
              <p class="text-lg font-bold text-[color:var(--color-on-surface)] tracking-tight">
                j
              </p>
            ) : (
              <>
                <p class="text-label font-medium tracking-widest uppercase text-[color:var(--color-on-surface-variant)]">
                  juunit GmbH
                </p>
                <p class="text-lg font-bold text-[color:var(--color-on-surface)] tracking-tight">
                  juuplan
                </p>
              </>
            )}
          </Link>
        </div>

        {/* Collapse-Toggle (Desktop) */}
        <button
          type="button"
          onClick={toggleCollapse}
          class={`hidden md:inline-flex items-center justify-center rounded mx-3 mb-2 p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors min-h-[32px] ${
            collapsed ? "self-center" : "self-start"
          }`}
          aria-label={collapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
          title={collapsed ? "Sidebar erweitern" : "Sidebar einklappen"}
        >
          {collapsed ? (
            <PanelLeftOpen size={16} aria-hidden="true" />
          ) : (
            <PanelLeftClose size={16} aria-hidden="true" />
          )}
        </button>

        {/* Hauptziele — genau `primaryNav()`, keine Liste in dieser Datei. */}
        <SidebarNavList
          ariaLabel="Hauptziele"
          class="px-2 mb-2 flex flex-col gap-0.5"
          targets={primaryNav()}
          location={location}
          collapsed={collapsed}
          close={close}
        />

        {/* Spacer when collapsed — keeps footer anchored at bottom. */}
        {collapsed && <div class="flex-1 hidden md:block" aria-hidden="true" />}

        {/* Listen- + Projekte-Blöcke share one scroll region so they read as
            one system rather than two. Hidden in collapsed rail. */}
        <div class={`flex-1 min-h-0 overflow-y-auto px-2 pb-2 ${collapsed ? "md:hidden" : ""}`}>
          {/* ⭐ Angeheftet — erste Sektion des Arbeitsbereichs, space-
              übergreifend und daher NICHT unter „Projekte" eingerückt. */}
          {renderFavoritesSection()}

          {/* Listen-Block — flach (Persönlich/Geteilt, keine Ordner) */}
          <div class="flex items-center">
            <button
              type="button"
              onClick={() => toggleProjectSection("listsRoot")}
              class="flex-1 flex items-center gap-1.5 pl-2 pr-1 pt-1 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors min-w-0"
              aria-expanded={!listsRootCollapsed}
            >
              {listsRootCollapsed ? (
                <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
              ) : (
                <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
              )}
              <ListTodo size={14} aria-hidden="true" class="shrink-0" />
              <span>Aufgabenlisten</span>
            </button>
            <button
              type="button"
              onClick={() => {
                setShowNewList((v) => !v);
                setNewListName("");
              }}
              class="mr-1 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors shrink-0"
              aria-label="Neue Liste"
              title="Neue Liste"
            >
              <Plus size={14} aria-hidden="true" />
            </button>
          </div>
          {/* Inline "Neue Liste" form */}
          {showNewList && (
            <div class="flex flex-col gap-1 px-2 pb-1">
              <input
                type="text"
                value={newListName}
                maxLength={100}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onInput={(e) => setNewListName((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void handleCreateList(); }
                  if (e.key === "Escape") { setShowNewList(false); setNewListName(""); }
                }}
                placeholder="Listenname…"
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-0.5 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name der neuen Liste"
              />
              <div class="flex items-center gap-1">
                {spaces.items.length > 0 && (
                  <select
                    value={newListSpaceId === undefined ? "" : String(newListSpaceId)}
                    onChange={(e) => {
                      const v = (e.target as HTMLSelectElement).value;
                      setNewListSpaceId(v === "" ? undefined : Number(v));
                    }}
                    class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-0.5 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                    aria-label="Space für neue Liste"
                  >
                    <option value="">Persönlich</option>
                    {spaces.items.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={() => void handleCreateList()}
                  disabled={!newListName.trim() || creatingList}
                  class="rounded bg-[color:var(--color-primary)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-50 transition-colors shrink-0"
                  aria-label="Liste erstellen"
                >
                  {creatingList ? "…" : "OK"}
                </button>
              </div>
            </div>
          )}

          {!listsRootCollapsed && (
            <div class="flex flex-col pb-2">
              <ul class="flex flex-col gap-0.5">
                <li>
                  <Link href="/lists" onClick={close} class={treeRowClass("pl-7", activeListSelection === "all")}>
                    <TreeRowContent
                      leading={<ListTodo size={14} aria-hidden="true" class="shrink-0 text-[color:var(--color-on-surface-variant)]" />}
                      label="Alle Aufgaben"
                    />
                  </Link>
                </li>
              </ul>

              <div class="px-3 pt-2 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)]">
                Persönlich
              </div>
              {listsLoading && lists.length === 0 ? (
                <div class="space-y-1 px-1">
                  {[0, 1].map((i) => (
                    <div key={i} class="h-7 rounded bg-[color:var(--color-surface-container-low)] animate-pulse" />
                  ))}
                </div>
              ) : (
                <ul class="flex flex-col gap-0.5 pb-1">
                  {personalLists.map((l) => (
                    <ListRow key={l.id} list={l} active={activeListSelection === l.id} close={close} />
                  ))}
                </ul>
              )}

              {sharedLists.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => toggleProjectSection("listsShared")}
                    class="w-full flex items-center gap-1.5 px-3 pt-2 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors"
                    aria-expanded={!listsSharedCollapsed}
                  >
                    {listsSharedCollapsed ? (
                      <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
                    ) : (
                      <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
                    )}
                    <span class="truncate">Geteilt</span>
                    <span class="ml-auto tabular-nums text-[color:var(--color-on-surface-variant)]/70">
                      {sharedLists.length}
                    </span>
                  </button>
                  {!listsSharedCollapsed && (
                    <ul class="flex flex-col gap-0.5 pb-1">
                      {sharedLists.map((l) => (
                        <ListRow key={l.id} list={l} active={activeListSelection === l.id} close={close} />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Parent-Header "Projekte" + "Neuer Space" affordance */}
          <div class="flex items-center">
            <button
              type="button"
              onClick={() => toggleProjectSection("root")}
              class="flex-1 flex items-center gap-1.5 pl-2 pr-1 pt-3 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] rounded transition-colors min-w-0"
              aria-expanded={!rootCollapsed}
            >
              {rootCollapsed ? (
                <ChevronRight size={14} aria-hidden="true" class="shrink-0" />
              ) : (
                <ChevronDown size={14} aria-hidden="true" class="shrink-0" />
              )}
              <Folder size={14} aria-hidden="true" class="shrink-0" />
              <span>Projekte</span>
            </button>
            {isInternal && (
              <button
                type="button"
                onClick={() => {
                  setShowNewSpace((v) => !v);
                  setNewSpaceName("");
                }}
                class="mt-2 mr-1 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors shrink-0"
                aria-label="Neuer Space"
                title="Neuer Space"
              >
                <Plus size={14} aria-hidden="true" />
              </button>
            )}
          </div>
          {/* Inline "Neuer Space" form */}
          {showNewSpace && isInternal && (
            <div class="flex items-center gap-1 px-2 pb-1">
              <input
                type="text"
                value={newSpaceName}
                maxLength={100}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                onInput={(e) => setNewSpaceName((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void handleCreateSpace(); }
                  if (e.key === "Escape") { setShowNewSpace(false); setNewSpaceName(""); }
                }}
                placeholder="Space-Name…"
                class="flex-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-0.5 text-base sm:text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2"
                aria-label="Name des neuen Spaces"
              />
              <button
                type="button"
                onClick={() => void handleCreateSpace()}
                disabled={!newSpaceName.trim() || creatingSpace}
                class="rounded bg-[color:var(--color-primary)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-50 transition-colors"
                aria-label="Space erstellen"
              >
                {creatingSpace ? "…" : "OK"}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewSpace(false); setNewSpaceName(""); }}
                class="rounded border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-label text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                aria-label="Abbrechen"
              >
                ✕
              </button>
            </div>
          )}

          {!rootCollapsed && (
            <>
              {projects.loading ? (
                <div class="space-y-1 px-1 pt-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      class="h-7 rounded bg-[color:var(--color-surface-container-low)] animate-pulse"
                    />
                  ))}
                </div>
              ) : visibleProjects.length === 0 ? (
                <p class="px-2 py-3 text-xs text-[color:var(--color-on-surface-variant)]">
                  Keine sichtbaren Projekte. Filter in den Einstellungen prüfen.
                </p>
              ) : (
                <div class="flex flex-col">
                  {/* Space groups — sorted by sort_order (already sorted in store) */}
                  {spaces.items.map((space) => (
                    <SpaceGroup
                      key={space.id}
                      space={space}
                      visibleProjects={visibleProjects}
                      activeProjectId={activeProjectId}
                      renderProjectLink={renderProjectLink}
                      close={close}
                      isInternal={isInternal}
                      onNewProject={(spaceId) => {
                        setNewProjectSpaceId(spaceId);
                        setShowCreate(true);
                      }}
                    />
                  ))}

                  {/* Projekte, deren Space der Nutzer nicht sieht: direkt
                      freigegeben (Projekt-Mitglied ohne Space-Zugriff). */}
                  {(() => {
                    const spaceIds = new Set(spaces.items.map((s) => s.id));
                    const orphaned = visibleProjects.filter(
                      (p) => !spaceIds.has(p.space_id)
                    );
                    if (orphaned.length === 0) return null;
                    return (
                      <div>
                        <div class="px-3 pt-2 pb-1 text-label font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)]">
                          Freigegebene Projekte
                        </div>
                        <ul class="flex flex-col gap-0.5 pb-1">
                          {orphaned.map((p) => renderProjectLink(p, { indent: true }))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {/* Create chooser: Projekt vs. Persönliche Aufgabe */}
        <div class="relative px-3 pb-2">
          <Button
            variant="primary"
            size="md"
            class="w-full"
            type="button"
            onClick={() => setShowCreateMenu((v) => !v)}
            title="Neu"
            aria-label="Neu"
            aria-haspopup="menu"
            aria-expanded={showCreateMenu}
          >
            <Plus size={14} aria-hidden="true" />
            {!collapsed && <span>Neu</span>}
          </Button>

          {showCreateMenu && (
            <>
            <div class="fixed inset-0 z-10" onClick={() => setShowCreateMenu(false)} aria-hidden="true" />
            <div
              role="menu"
              class="absolute left-3 right-3 bottom-full mb-1 z-20 flex flex-col gap-0.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-1 shadow-level-2"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowCreate(true);
                  setShowCreateMenu(false);
                  close();
                }}
                class="inline-flex items-center gap-2 rounded px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors min-h-[32px]"
              >
                <Folder size={16} aria-hidden="true" />
                <span>Projekt</span>
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowCreatePersonalTask(true);
                  setShowCreateMenu(false);
                  close();
                }}
                class="inline-flex items-center gap-2 rounded px-2.5 py-1.5 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors min-h-[32px]"
              >
                <ListTodo size={16} aria-hidden="true" />
                <span>Aufgabe</span>
              </button>
            </div>
            </>
          )}
        </div>

        {/* Fussbereich — genau `footerNav(isAdmin)`. Der Nutzer-Block
            (Avatar/Name/E-Mail/Abmelden) sitzt jetzt in der Kopfzeile. */}
        <SidebarNavList
          ariaLabel="Einstellungen und Verwaltung"
          class="px-2 pb-3 flex flex-col gap-0.5 border-t border-[color:var(--color-outline-variant)] pt-2"
          targets={footerNav(me.user?.is_admin ?? false)}
          location={location}
          collapsed={collapsed}
          close={close}
        />
      </aside>

      {showCreate && (
        <NewProjectModal
          defaultSpaceId={newProjectSpaceId}
          onClose={() => { setShowCreate(false); setNewProjectSpaceId(undefined); }}
          onCreated={(id) => {
            setShowCreate(false);
            setNewProjectSpaceId(undefined);
            setLocation(`/p/${id}`);
          }}
        />
      )}

      {showCreatePersonalTask && (
        <NewPersonalTaskModal
          onClose={() => setShowCreatePersonalTask(false)}
          onCreated={() => {
            setShowCreatePersonalTask(false);
            setLocation("/lists");
          }}
        />
      )}
    </>
  );
}
